// src/hooks/useWorkspaces.ts
import { useCallback, useEffect, useState } from "react";
import {
  fetchWorkspaces,
  getActiveWorkspaceId,
  setActiveWorkspaceId,
  Workspace,
} from "../services/workspaceApi";
import { setActiveCurrency } from "../services/currency";

/**
 * Businesses the signed-in user can switch between. Most users have one, so
 * the switcher stays hidden; employees who also run their own business get two.
 */
export const useWorkspaces = (enabled: boolean) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<string | null>(
    getActiveWorkspaceId()
  );

  useEffect(() => {
    if (!enabled) {
      setWorkspaces([]);
      return;
    }
    let cancelled = false;
    fetchWorkspaces()
      .then(({ workspaces: list, defaultWorkspaceId }) => {
        if (cancelled) return;
        setWorkspaces(list);
        const saved = getActiveWorkspaceId();
        const savedWorkspace = saved ? list.find((w) => w.id === saved) : undefined;
        if (savedWorkspace) {
          // Money shows in this business's currency (an employer's when
          // working for one).
          setActiveCurrency(savedWorkspace.currency);
          return;
        }
        const fallback = defaultWorkspaceId ?? list[0]?.id ?? null;
        setActiveCurrency(list.find((w) => w.id === fallback)?.currency);
        setActiveWorkspaceId(fallback);
        setActiveId(fallback);
        // A saved business we can no longer use: reload so every page
        // refetches with the default one.
        if (saved && saved !== fallback) window.location.reload();
      })
      .catch(() => {
        // Keep the app usable without the switcher.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const switchTo = useCallback(
    (workspaceId: string) => {
      if (workspaceId === activeId) return;
      setActiveWorkspaceId(workspaceId);
      // A full reload clears Redux and every page's cached data, and lands
      // on Events, which both owners and employees can open.
      window.location.assign("/events");
    },
    [activeId]
  );

  const active = workspaces.find((w) => w.id === activeId) ?? null;
  return { workspaces, active, switchTo };
};
